import React, {
  useContext,
  useState,
  createContext,
  ReactNode,
  useEffect
} from 'react';
import { selectElement } from '../sidebar/views/CreateTask/selector';
import { Coordinates } from '../sidebar/views/CreateTask/create_task/types/task';
import { TagInfoType } from '../sidebar/views/CreateTask/selector/types';
import {
  generateCoordinates,
  prepareTagInfo
} from '../sidebar/views/CreateTask/selector/parseNode';
import { ResourceUserCamelized, SimpleProjectCamelized } from '../embed/auth';
import { get } from '../../redesign/javascript/utils/fetch';
import { PreCreatedTask } from '../sidebar/views/CreateTask';
import browserSpecs from '../sidebar/views/CreateTask/browserSpecs';
import getTaskHostPath from '../shared/utils/getTaskHostPath';
import { locationFromConfig } from '../shared/utils/locationFromConfig';
import camelCaseKeys from 'camelcase-keys';
import { useAnalyticsState } from './Analytics';
import { sendScreenshotData } from 'appJS/screenshotServer';

// @ts-ignore
const CreateTaskStateContext = createContext();

type AttachmentType = 'screenshot' | 'recording';

type S3Response = {
  S3URL: string;
  key: string;
  policy: string;
  signature: string;
  success?: string;
};

type Screenshot = {
  screenshotPolyfill: any;
  error?: string;
} | null;

type TaskType = 'element' | 'page' | 'recording' | null;

const CreateTaskProvider = ({
  apiDomain,
  project,
  children,
  user
}: {
  apiDomain: string;
  project: SimpleProjectCamelized;
  children: ReactNode;
  user: ResourceUserCamelized;
}) => {
  const {
    projectDetailsUrl,
    screenshotsDisabled,
    sites,
    mobileScreenshotsAllowed,
    urls,
    screenshotServerUrl
  } = project;

  // We are doing this to avoid an error: "regeneratorRuntime is not defined"
  const {
    captureScreenshot
  } = require('../sidebar/views/CreateTask/captureScreenshot');
  const {
    updateTaskScreenshot
  } = require('../../javascript/screenshotCapture');

  const { analytics } = useAnalyticsState();

  const windowLocation = locationFromConfig();

  const { pathname, hostname } = getTaskHostPath({
    projectSites: sites,
    projectUrls: urls,
    windowLocation
  });

  const [screenshot, setScreenshot] = useState<Screenshot>(null);
  const [tagInfo, setTagInfo] = useState<TagInfoType | {}>({});
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [userMetaData, setUserMetaData] = useState<{}>({});
  const [recording, setRecording] = useState<Blob>();
  const [type, setType] = useState<TaskType>(null);
  const [
    s3ResponseForScreenshotServer,
    setS3ResponseForScreenshotServer
  ] = useState<S3Response | null>(null);

  useEffect(() => {
    if (screenshot !== null) {
      (async () => {
        const s3Response = await requestNewAttachmentSlot(
          'screenshot.jpg',
          'screenshot'
        );

        setS3ResponseForScreenshotServer(s3Response);

        // Fire and forget so we don't hold up the task creation
        sendScreenshotData({
          bugherdUrl: apiDomain,
          projectId: project.id,
          polyfillData: { ...screenshot?.screenshotPolyfill, s3Response }
        }).catch(e => console.error('unknown error', e));
      })();
    }
  }, [screenshot]);

  const clearTaskData = () => {
    setScreenshot(null);
    setTagInfo({});
    setCoordinates(null);
    setUserMetaData({});
    setRecording(undefined);
    setType(null);
  };

  const setTaskAttributes = (_tagInfo?: TagInfoType, element?: HTMLElement) => {
    if (_tagInfo) {
      setTagInfo(type === 'element' ? _tagInfo : {});
      if (element) {
        setCoordinates(
          // @ts-ignore
          generateCoordinates(
            element,
            _tagInfo.data.bugOffsetX,
            _tagInfo.data.bugOffsetY
          ) as Coordinates
        );
      }
    }
    setUserMetaData(window?.BugHerdConfig?.metadata || {});
  };

  const handleElement = (
    element: HTMLElement,
    mouseX: number,
    mouseY: number
  ) => {
    const _tagInfo = prepareTagInfo(element, mouseX, mouseY);

    try {
      setTaskAttributes(_tagInfo, element);
      captureScreenshot({
        taskMobileScreenshot: mobileScreenshotsAllowed && !screenshotsDisabled,
        backgroundColor: window.getComputedStyle(document.body).backgroundColor,
        callback: setScreenshot,
        extensionExists: false,
        basicAuthPresent: !!projectDetailsUrl,
        projectDetailsUrl,
        userId: user.id,
        organizationId: user.organizationId
      });
    } catch (err) {
      console.error(err);
    }
  };

  const initialiseTask = (onCancel: (event: KeyboardEvent) => void) => {
    requestAnimationFrame(() => {
      if (type === 'element') {
        selectElement(handleElement, onCancel);
      } else if (type === 'page') {
        handleElement(document.documentElement, 0, 0);
      } else if (type === 'recording') {
        setTaskAttributes();
      } else {
        console.error('SELECT_ELEMENT type is not supported');
      }
    });
  };

  const requestNewAttachmentSlot = (
    filename: string,
    type: AttachmentType
  ): Promise<S3Response> =>
    get(
      `${apiDomain}/projects/${project.id}/new_task_attachment?filename=${filename}&${type}=true`
    );

  const createTask = async (
    task: PreCreatedTask,
    onSuccess: () => void,
    onLoading: () => void,
    onError: (errorMessage: string) => void,
    onUploading: () => void,
    onProgress?: (
      loaded: number,
      total: number,
      lengthComputable: boolean
    ) => void
  ) => {
    if (onLoading) onLoading();

    analytics.track('Create task started', { source: 'public_feedback' });

    task.screenshot_type = 'standard';
    task.data = {
      coordinates,
      userMetaData
    };

    let _type: AttachmentType = 'screenshot';
    let fileName = `${_type}.jpg`;
    if (recording) {
      _type = 'recording';
      fileName = `${_type}${
        recording?.type.includes('webm') ? '.webm' : '.mp4'
      }`;
      task.screenshot_type = 'video';
      task.data = null;
    }

    let screenshotDataURI;

    let s3Response = s3ResponseForScreenshotServer;

    if (!s3Response) {
      s3Response = await requestNewAttachmentSlot(fileName, _type);
    }

    if (!screenshotsDisabled) {
      screenshotDataURI = task.dataURI;
    }

    Reflect.deleteProperty(task, 'dataURI');

    task.created_at = new Date().toISOString();
    task.screenshot_key = s3Response.key;
    task.browser_info = browserSpecs();
    task.selector_info = tagInfo;
    task.site = hostname;
    task.url = pathname;

    window.onbeforeunload = function(e) {
      var msg = 'A task is still being created';
      // IE, gecko and webkit all do this different
      (e || window.event).returnValue = msg;
      return msg;
    };

    fetch(`${apiDomain}/projects/${project.id}/tasks`, {
      method: 'POST',
      body: JSON.stringify(task),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(res => res.json())
      .then(dbTask => {
        dbTask = camelCaseKeys(dbTask);

        if (onSuccess) onSuccess();

        if (!screenshotsDisabled) {
          s3Response.success = `${apiDomain}/sidebar/projects/${project.id}/tasks/${dbTask.id}/set_screenshot_callback`;
          updateTaskScreenshot({
            taskId: dbTask.id,
            isScreenshotServerAccess: mobileScreenshotsAllowed,
            s3Response,
            polyfillData: screenshot?.screenshotPolyfill,
            dataURI: screenshotDataURI,
            dataRecording: recording,
            captureError: screenshot?.error || 'capture error',
            uploadState: {
              onProgress,
              onStart: recording ? onUploading : null
            },
            screenshotServerUrl,
            projectId: project.id,
            bugherdUrl: apiDomain,
            userId: user.id
          })
            .then(() => {
              window.onbeforeunload = null;
            })
            .catch(err => {
              window.onbeforeunload = null;
              window.bugsnagClient
                ? window.bugsnagClient.notify(err)
                : console.error(err);
              if (onError) onError('Could not upload screenshot');
            });
        }
      })
      .catch(err => {
        window.onbeforeunload = null;
        window.bugsnagClient
          ? window.BugHerdConfig.notify(err)
          : console.error(err);

        if (onError)
          onError('Could not create a task, please reload the page.');
        analytics.track('Create task failed', { source: 'public_feedback' });
      });
  };

  return (
    <CreateTaskStateContext.Provider
      value={{
        createTask,
        initialiseTask,
        setTaskAttributes,
        onCreateTask: () => setType('element'),
        onCreateFullPageTask: () => setType('page'),
        onCreateRecording: () => setType('recording'),
        type,
        tagInfo,
        recording,
        setRecording,
        clearTaskData,
        apiDomain,
        project
      }}
    >
      {children}
    </CreateTaskStateContext.Provider>
  );
};

type CreateTaskState = {
  createTask: () => void;
  initialiseTask: (onCancel: (event: KeyboardEvent) => void) => void;
  setTaskAttributes: () => void;
  onCreateTask: () => void;
  onCreateFullPageTask: () => void;
  onCreateRecording: () => void;
  type: TaskType;
  tagInfo: TagInfoType;
  recording: Blob;
  setRecording: (recording: Blob) => void;
  clearTaskData: () => void;
  apiDomain: string;
  project: SimpleProjectCamelized;
};

const useCreateTaskState = (): CreateTaskState => {
  const context = useContext(CreateTaskStateContext);

  if (context === undefined) {
    throw new Error('useCreateTaskState must be used within a ProjectProvider');
  }

  // @ts-ignore
  return context;
};

export { CreateTaskProvider, useCreateTaskState };
